import type { Node } from '@atlaskit/editor-prosemirror/model';

import { getTableEstimatedHeight } from '../../utils/node-estimated-height.ts';

export type TableBatchEnds = {
	/** Rows after which a height batch ends. */
	rows: ReadonlySet<Node>;
	lastBatchHeight: number;
};

/**
 * Ends height batches between the rows of a full top-level table, so a long table does not render
 * whole in one batch. Row heights come from the ADF alone, so SSR, hydration and the light plan cut
 * at the same rows.
 */
export function getTableBatchEnds(
	table: Node,
	startHeight: number,
	lastBatchHeight: number,
	batchHeight: number,
): TableBatchEnds {
	const rows = new Set<Node>();
	let height = startHeight;
	let coveredThrough = -1;
	// The table's own top-level boundary covers a batch that ends with its last row.
	for (let index = 0; index < table.childCount - 1; index++) {
		const row = table.child(index);
		height += getTableEstimatedHeight(row);
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity -- Visits each cell once; work is linear in the number of cells.
		row.forEach((cell) => {
			coveredThrough = Math.max(coveredThrough, index + (cell.attrs.rowspan || 1) - 1);
		});
		// A cell must stay in the same batch as every row it spans.
		if (index >= coveredThrough && height - lastBatchHeight >= batchHeight) {
			rows.add(row);
			lastBatchHeight = height;
		}
	}
	return { rows, lastBatchHeight };
}
